import type { AxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';
import type {
  FormResponse, FormQuery, CreateFormRequest, UpdateFormRequest,
  FormSubmissionResponse, FormSubmissionQuery, FormStats, FormStatsQuery,
  FormPublicData, SubmitFormRequest
} from '@troia-v3/types';

interface FormListResponse {
  items: FormResponse[];
  total: number;
  page: number;
  totalPages: number;
}

interface FormSubmissionListResponse {
  items: FormSubmissionResponse[];
  total: number;
  page: number;
  totalPages: number;
}

export const formsApi = {
  // ============================================================================
  // FORM CRUD
  // ============================================================================

  async getAll(query?: FormQuery): Promise<FormListResponse> {
    const response = await api.get('/forms', { params: query });
    return response.data.data;
  },

  async getById(id: string): Promise<FormResponse> {
    const response = await api.get(`/forms/${id}`);
    return response.data.data;
  },

  async create(data: CreateFormRequest): Promise<FormResponse> {
    const response = await api.post('/forms', data);
    return response.data.data;
  },

  async update(id: string, data: UpdateFormRequest): Promise<FormResponse> {
    const response = await api.put(`/forms/${id}`, data);
    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/forms/${id}`);
  },

  async duplicate(id: string): Promise<FormResponse> {
    const response = await api.post(`/forms/${id}/duplicate`);
    return response.data.data;
  },

  /** GET /api/forms/:id/fields?all= — campos do modelo (cross-module, app+auth). */
  async getFields(id: string, all = false): Promise<{ id: string; name: string; type: FormResponse['type']; fields: FormResponse['fields'] }> {
    const response = await api.get(`/forms/${id}/fields`, { params: all ? { all: 'true' } : undefined });
    return response.data.data;
  },

  /** GET /api/forms/:id/usage — quantas aplicações usam o modelo (aviso do construtor). */
  async getUsage(id: string): Promise<{ applications: number }> {
    const response = await api.get(`/forms/${id}/usage`);
    return response.data.data;
  },

  async activate(id: string): Promise<FormResponse> {
    const response = await api.patch(`/forms/${id}/activate`);
    return response.data.data;
  },

  async deactivate(id: string): Promise<FormResponse> {
    const response = await api.patch(`/forms/${id}/deactivate`);
    return response.data.data;
  },

  // ============================================================================
  // SUBMISSIONS
  // ============================================================================

  async getResponses(formId: string, query?: Partial<FormSubmissionQuery>): Promise<FormSubmissionListResponse> {
    const response = await api.get(`/forms/${formId}/responses`, { params: query });
    return response.data.data;
  },

  async getResponse(formId: string, responseId: string): Promise<FormSubmissionResponse> {
    const response = await api.get(`/forms/${formId}/responses/${responseId}`);
    return response.data.data;
  },

  async deleteResponse(formId: string, responseId: string): Promise<void> {
    await api.delete(`/forms/${formId}/responses/${responseId}`);
  },

  async exportResponses(formId: string): Promise<FormSubmissionResponse[]> {
    const response = await api.get(`/forms/${formId}/responses/export`);
    return response.data.data;
  },

  // ============================================================================
  // STATS
  // ============================================================================

  async getStats(formId: string, query?: FormStatsQuery): Promise<FormStats> {
    const response = await api.get(`/forms/${formId}/stats`, { params: query });
    return response.data.data;
  },

  // ============================================================================
  // PUBLIC
  // ============================================================================

  async getPublicForm(slug: string): Promise<FormPublicData> {
    const response = await api.get(`/forms/public/${slug}`);
    return response.data.data;
  },

  async submitPublicForm(slug: string, data: SubmitFormRequest): Promise<FormSubmissionResponse> {
    // A página pública mostra o erro por campo (422 do bloco de contato) —
    // sem o aviso genérico por cima (mesmo padrão da captura por QR).
    const config: AxiosRequestConfig & { _handleErrorLocally: boolean } = { _handleErrorLocally: true };
    const response = await api.post(`/forms/public/${slug}/submit`, data, config);
    return response.data.data;
  },
};
